import { FunctionComponent, MutableRefObject } from 'react';
import { DraftDesignConfiguration, VectorDesign } from '@/types';
import { getDesignImageUrl } from '@/utils/imageUrls/getDesignImageUrl';
import { scaleSize } from '@/model/configuration/scaleSize';
import { isVectorDesign } from '@/model/design/isDesignType';
import { useLoadImages } from '../../hooks/useLoadImages';
import { getFillValue } from '../../utils/getFillValue';
import { PatternList } from '../PatternList';
import { SoftBoundary } from '../SoftBoundary';

interface ImageRendererProps {
  configuration: DraftDesignConfiguration;
  design: VectorDesign;
  width: number;
  height: number;
}

const VectorDesignRenderer: FunctionComponent<ImageRendererProps> = ({
  configuration,
  design,
  width,
  height,
}) => {
  const layerImages = useLoadImages(design);

  if (!layerImages.length) {
    return <rect width={width} height={height} />;
  }

  return (
    <>
      <PatternList configuration={configuration} width={width} height={height} />
      {design.colors.map((color, i) => (
        <mask id={`C-${configuration.cid}-${i}`} key={`C-${configuration.cid}-${i}`}>
          <image xlinkHref={layerImages[color.layer].src} width={width} height={height} />
        </mask>
      ))}
      {configuration.printColors.map((color, i) => (
        <rect
          mask={`url(#C-${configuration.cid}-${i})`}
          fill={getFillValue(configuration, design, i)}
          width={width}
          height={height}
          key={i}
        />
      ))}
    </>
  );
};

export const useDesign = (
  configuration: DraftDesignConfiguration,
  designElementRef?: MutableRefObject<SVGGElement | null>,
  offset = configuration.offset,
  rotation = configuration.rotation,
  scale = configuration.scale
) => {
  const design = configuration.design;
  const configurationSize = configuration.size;
  const scaledConfigurationSize = scaleSize(configurationSize, scale);
  const transformScale = `scale(${scale}, ${scale})`;

  const { width, height } = configurationSize;

  const designElement = (
    <SoftBoundary
      cid={configuration.cid}
      printArea={configuration.printArea}
      rotation={rotation}
      size={scaledConfigurationSize}
      offset={offset}
    >
      <g transform={transformScale} ref={designElementRef}>
        {isVectorDesign(design) && !design.restrictions.fixedColors ? (
          <VectorDesignRenderer
            configuration={configuration}
            design={design}
            width={width}
            height={height}
          />
        ) : (
          <image
            xlinkHref={configuration.processedImage || getDesignImageUrl(design.id, { width: 600 })}
            width={width}
            height={height}
          />
        )}
      </g>
    </SoftBoundary>
  );

  const transform = `translate(${offset.x}, ${offset.y}) rotate(${rotation}, ${
    scaledConfigurationSize.width / 2
  }, ${scaledConfigurationSize.height / 2})`;

  return [designElement, transform] as const;
};
